import { Fragment, FunctionComponent } from 'react'
import styled, { css, useTheme } from 'styled-components'
import { CookWidget, JSONTypeForCookWidget } from '@widgets/types'
import { Link } from '@utils/cook/types'
import Item from './Item'
import usePageMetadata from '@hooks/usePageMetadata'
import { desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'
import SvgIcon from '@components/SvgIcon'

export interface BreadcrumbItem {
  title: string
  uniqueName?: string
  link?: Link
  isLast: boolean
}

interface BreadcrumbProps {
  children: BreadcrumbItem[]
  options: {
    pageType: string
  }
}

interface ScrollWrapperProps {
  isDossierPage: boolean
}

const ScrollWrapper = styled.div<ScrollWrapperProps>`
  ${({
    theme: {
      colors: { fillWeak },
      spacing: { spacing12, spacing40, spacing16, spacing20 },
    },
    isDossierPage,
  }) => css`
    background-color: ${fillWeak};
    display: flex;
    flex-direction: row;
    white-space: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    padding: ${spacing12} ${spacing40};
    box-sizing: border-box;
    text-align: center;
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    &::-webkit-scrollbar {
      display: none;
    }

    ${mobileCSS(css`
      padding: 14px ${spacing16};
      background-color: transparent;
    `)}

    ${!isDossierPage &&
    css`
      ${desktopCSS(css`
        margin-right: ${spacing20};
        margin-left: ${spacing20};
      `)};
      ${tabletCSS(css`
        margin-right: 68px;
        margin-left: 68px;
      `)};
      ${mobileCSS(css`
        margin-right: ${spacing16};
        margin-left: ${spacing16};
      `)};
    `}
  `}
`

const StyledBreadcrumbContainer = styled.nav`
  ${({
    theme: {
      colors: { textWeak },
      typography: {
        subheads: {
          small2: { bundledCSS: small2SubheadCSS },
        },
      },
      spacing: { spacing4, spacing8 },
    },
  }) => css`
    ${small2SubheadCSS};
    color: ${textWeak};
    grid-column: span 6;
    display: flex;
    flex: 1;
    flex-direction: row;
    white-space: nowrap;
    text-align: center;
    gap: ${spacing4};

    ${desktopCSS(css`
      gap: ${spacing8};
    `)}
  `}
`

const Breadcrumb: FunctionComponent<BreadcrumbProps> = ({ children }) => {
  const theme = useTheme()
  const lastIndex = children.length
  const { context } = usePageMetadata()
  const isDossierPage = context === 'dossier'

  const Separator: FunctionComponent<{ index: number }> = ({ index }) =>
    ![0, lastIndex].includes(index) ? (
      <SvgIcon
        iconName="chevron-right"
        color={theme.colors.iconDefault}
        size={16}
      />
    ) : null

  return (
    <ScrollWrapper isDossierPage={isDossierPage}>
      <StyledBreadcrumbContainer>
        {children.map((crumb, index, crumbsArray) => (
          <Fragment key={index}>
            <Separator index={index} />
            <Item {...crumb} isLast={index === crumbsArray.length - 1} />
          </Fragment>
        ))}
      </StyledBreadcrumbContainer>
    </ScrollWrapper>
  )
}

const widget = {
  kind: ['widget', 'breadcrumb'],
  component: Breadcrumb,
} as const satisfies CookWidget

export type WidgetType = typeof widget

export type JSONWidgetType = JSONTypeForCookWidget<WidgetType>

export default widget
